// Read-time provenance backfill for profiles written before 2026-09-05.
//
// Existing documents have no `dimensionProvenance` field. Rather than run a
// migration job (there is no live traffic; the population is JB's own test
// accounts), provenance is inferred on read:
//
//   - a non-null paid dimension was scored somehow → `inferred`
//   - a null paid dimension was never scored       → `not_assessed`
//   - free dimensions are never null, so nullness cannot distinguish a real score
//     from a fallback. They are marked `inferred` — EXCEPT on a profile carrying
//     the fabrication signature below, where they are marked `defaulted`.
//
// The fabrication signature is the fingerprint of the live defect: every free
// dimension sitting on its exact hard-coded fallback value. That combination
// cannot arise from a real diagnostic, so a profile showing it is telling us its
// numbers were invented. See docs/intent/001-diagnostic-redesign/spec.md §0.

import type { CogniPrintProfile, DiagnosticDimensionKey, DimensionProvenance } from '../types/cogniprint';

/** The exact fallback values transformAIProfile() substitutes when a payload is empty. */
function looksFabricated(profile: CogniPrintProfile): boolean {
  const cf = profile.contentFormat?.score;
  const la = profile.learningApproach?.score;
  const sp = profile.sequencing?.score;
  const allFormatsEven =
    !!cf && cf.visual === 20 && cf.textual === 20 && cf.auditory === 20 &&
    cf.interactive === 20 && cf.video === 20;
  const axesDeadCentre = !!la && la.theoryApplication === 50 && la.reflectiveActive === 50;
  const sequencingEven = !!sp && sp.topDown === 34 && sp.bottomUp === 33 && sp.modular === 33;
  return allFormatsEven && axesDeadCentre && sequencingEven;
}

export function backfillProvenance(profile: CogniPrintProfile): CogniPrintProfile {
  if (profile.dimensionProvenance) return profile;

  const freeProvenance: DimensionProvenance = looksFabricated(profile) ? 'defaulted' : 'inferred';
  const paid = (v: unknown): DimensionProvenance => (v == null ? 'not_assessed' : 'inferred');

  const dimensionProvenance: Partial<Record<DiagnosticDimensionKey, DimensionProvenance>> = {
    contentFormat: freeProvenance,
    learningApproach: freeProvenance,
    depthTolerance: freeProvenance,
    sequencing: freeProvenance,
    infoDensity: paid(profile.infoDensity),
    motivation: paid(profile.motivation),
    viewpointRange: paid(profile.viewpointRange),
  };

  return { ...profile, dimensionProvenance };
}
